import './CategoryFilter.sass';

import { FC, useState, useMemo } from 'react';

import { ActionBottomSheet, Button } from '$uikit';
import { IcCheck16 } from '$assets';
import { classNames } from '$utils';
import { useTranslator } from '$hooks';

import type { EarningsCategoryMeta, IncomeType } from '../StatisticsPro.types';

const OTHERS_COLOR = '#9b9ba0';
const ALL_COLOR = '#3478f6';

export interface CategoryFilterProps {
  categories: EarningsCategoryMeta[];
  selected: IncomeType[];
  onToggle: (type: IncomeType) => void;
  onReset: () => void;
}

const REFERRAL_TYPES = new Set<IncomeType>([
  'referral_profit',
  'referral_profit_message',
  'referral_profit_tips',
  'referral_profit_bundle',
  'referral_profit_subscription',
  'referral_profit_sponsor_subscription',
  'offer_share',
]);

const isReferral = (type: IncomeType) => REFERRAL_TYPES.has(type);

export const CategoryFilter: FC<CategoryFilterProps> = ({
  categories,
  selected,
  onToggle,
  onReset,
}) => {
  const t = useTranslator();
  const [otherOpen, setOtherOpen] = useState(false);

  const pinnedCats = useMemo(() => categories.filter((c) => c.pinned), [categories]);
  const nonPinnedCats = useMemo(() => categories.filter((c) => !c.pinned), [categories]);

  const selectedNonPinnedChips = useMemo(
    () => nonPinnedCats.filter((c) => selected.includes(c.type)),
    [nonPinnedCats, selected],
  );

  const hiddenCount = categories.length - selected.length;

  const canReset = useMemo(() => {
    if (selected.length !== pinnedCats.length) return true;
    return pinnedCats.some((c) => !selected.includes(c.type));
  }, [pinnedCats, selected]);

  const renderChip = (cat: EarningsCategoryMeta) => {
    const isActive = selected.includes(cat.type);
    return (
      <Button
        key={cat.type}
        mode={isActive ? 'secondary' : 'inline'}
        size="extra-small"
        className={classNames('CategoryFilter__chip', { active: isActive })}
        onClick={() => onToggle(cat.type)}
        icon={
          <div className="CategoryFilter__chip__dot_container">
            <span
              className="CategoryFilter__chip__dot"
              style={{ backgroundColor: cat.color }}
            />
          </div>
        }
      >
        {cat.label}
      </Button>
    );
  };

  return (
    <div className="CategoryFilter">
      <div className="CategoryFilter__row">
        {pinnedCats.map(renderChip)}
        {selectedNonPinnedChips.map(renderChip)}
        <Button
          mode={hiddenCount > 0 ? 'secondary' : 'inline'}
          size="extra-small"
          className={classNames('CategoryFilter__chip', 'other', {
            active: hiddenCount > 0,
          })}
          onClick={() => setOtherOpen(true)}
          icon={
            <div className="CategoryFilter__chip__dot_container">
              <span
                className="CategoryFilter__chip__dot"
                style={{
                  backgroundColor: selected.length === 0 ? ALL_COLOR : OTHERS_COLOR,
                }}
              />
            </div>
          }
        >
          {selected.length === 0
            ? `${t('statistics_pro_all')} · ${hiddenCount}`
            : hiddenCount > 0
            ? `${t('statistics_pro_others')} · ${hiddenCount}`
            : t('statistics_pro_others')}
        </Button>
      </div>

      <ActionBottomSheet
        isOpened={otherOpen}
        title={t('statistics_pro_categories')}
        onChange={(v) => onToggle(v as IncomeType)}
        onClose={() => setOtherOpen(false)}
        options={[
          ...pinnedCats.map((cat) => ({
            value: cat.type,
            label: cat.label,
            icon: selected.includes(cat.type) ? <IcCheck16 /> : undefined,
            groupId: 'general',
          })),
          ...nonPinnedCats
            .filter((cat) => !isReferral(cat.type))
            .map((cat) => ({
              value: cat.type,
              label: cat.label,
              icon: selected.includes(cat.type) ? <IcCheck16 /> : undefined,
              groupId: 'general',
            })),
          ...nonPinnedCats
            .filter((cat) => isReferral(cat.type))
            .map((cat) => ({
              value: cat.type,
              label: cat.label,
              icon: selected.includes(cat.type) ? <IcCheck16 /> : undefined,
              groupId: 'referral',
            })),
        ]}
        groups={[{ id: 'referral', label: t('statistics_pro_group_referral') }]}
        footer={
          <>
            <Button size="medium" stretched onClick={() => setOtherOpen(false)}>
              {t('statistics_pro_apply')}
            </Button>
            <Button
              size="medium"
              mode={canReset ? 'destructive' : 'inline'}
              stretched
              disabled={!canReset}
              onClick={onReset}
            >
              {t('statistics_pro_reset')}
            </Button>
          </>
        }
      />
    </div>
  );
};
